import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { fetchAllActiveMentorshipOfferings } from '@/services/mentorshipService';
import { MentorshipOffering } from '@/types/mentorOffering';
import { getMultipleMentorRatings, MentorRating } from '@/services/reviewService';
import { useToast } from '@/hooks/use-toast';

export const useMentoriasPage = () => {
  const [mentorships, setMentorships] = useState<MentorshipOffering[]>([]);
  const [filteredMentorships, setFilteredMentorships] = useState<MentorshipOffering[]>([]);
  const [mentorRatings, setMentorRatings] = useState<Record<string, MentorRating>>({});
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedGameId, setSelectedGameId] = useState<string | null>(null);
  const [selectedType, setSelectedType] = useState<'all' | 'individual' | 'group'>('all');
  const [games, setGames] = useState<Array<{ id: string; name: string }>>([]);
  
  const navigate = useNavigate();
  const { toast } = useToast();
  const [searchParams] = useSearchParams();

  // Ler parâmetro de jogo da URL ao inicializar
  useEffect(() => {
    const gameParam = searchParams.get('game');
    if (gameParam) {
      setSelectedGameId(gameParam);
      console.log('[useMentoriasPage] Filtro de jogo aplicado da URL:', gameParam);
    }
  }, [searchParams]);

  useEffect(() => {
    console.log("useMentoriasPage hook initialized");
    fetchMentorships();
  }, []);

  useEffect(() => {
    if (mentorships.length > 0) {
      filterMentorships();
    }
  }, [mentorships, searchTerm, selectedGameId, selectedType]);

  const fetchMentorships = async () => {
    setIsLoading(true);
    try {
      console.log("Starting to fetch all active mentorships in useMentoriasPage hook...");
      const data = await fetchAllActiveMentorshipOfferings();
      console.log("Received mentorship data in hook:", data.length, "items");
      
      if (data.length === 0) {
        console.warn("No mentorship offerings found. This might indicate an RLS policy issue or empty database.");
        toast({
          title: "Atenção",
          description: "Não foram encontradas mentorias ativas. Isso pode indicar um problema de permissões ou que não existem mentorias cadastradas.",
          variant: "default"
        });
      } else {
        console.log("First mentorship offering sample:", data[0]);
        console.log("Mentor name from first offering:", data[0].mentor_name);
        
        // Buscar avaliações dos mentores
        const mentorIds = Array.from(new Set(data.map(offering => offering.mentor_id).filter(Boolean))) as string[];
        console.log("Fetching ratings for mentors:", mentorIds);
        
        if (mentorIds.length > 0) {
          const ratings = await getMultipleMentorRatings(mentorIds);
          console.log("Received mentor ratings:", ratings);
          setMentorRatings(ratings);
        }
      }
      
      setMentorships(data);
      setFilteredMentorships(data);

      // Extract unique games with proper typing
      const uniqueGames: Array<{ id: string; name: string }> = Array.from(
        new Map(
          data
            .filter((offering) => offering.game)
            .map((offering) => [
              offering.game!.id, 
              { id: offering.game!.id, name: offering.game!.name }
            ])
        ).values()
      );
      
      setGames(uniqueGames);
    } catch (err) {
      const error = err instanceof Error ? err : new Error('Unknown error occurred');
      setError(error);
      console.error('Error fetching mentorships:', err);
      
      toast({
        title: "Erro",
        description: `Erro ao carregar mentorias: ${error.message}`,
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  };

  const filterMentorships = () => {
    let filtered = [...mentorships];

    // Filter by search term
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(
        (mentorship) =>
          mentorship.title.toLowerCase().includes(term) ||
          mentorship.description.toLowerCase().includes(term) ||
          (mentorship.mentor_name && mentorship.mentor_name.toLowerCase().includes(term)) ||
          (mentorship.game?.name && mentorship.game.name.toLowerCase().includes(term))
      );
    }

    // Filter by game
    if (selectedGameId) {
      filtered = filtered.filter((mentorship) => mentorship.game?.id === selectedGameId);
    }

    // Filter by session type
    if (selectedType !== 'all') {
      filtered = filtered.filter((mentorship) => {
        if (selectedType === 'individual') return !mentorship.is_group;
        if (selectedType === 'group') return mentorship.is_group;
        return true;
      });
    }

    setFilteredMentorships(filtered);
  };

  const handleSearchChange = (value: string) => {
    setSearchTerm(value);
  };

  const handleGameFilterChange = (gameId: string | null) => {
    setSelectedGameId(gameId);
    
    // Atualizar URL com parâmetro de jogo
    if (gameId) {
      navigate(`/mentorias?game=${gameId}`, { replace: true });
      console.log('[useMentoriasPage] Navegando para mentorias com filtro de jogo:', gameId);
    } else {
      navigate('/mentorias', { replace: true });
      console.log('[useMentoriasPage] Removendo filtro de jogo da URL');
    }
  };

  const handleTypeFilterChange = (type: 'all' | 'individual' | 'group') => {
    setSelectedType(type);
  };

  const handleCardClick = (mentorId: string, mentorSlug?: string | null) => {
    if (!mentorId) {
      console.error('Attempted to navigate to mentor profile with no mentorId');
      return;
    }
    
    // Use slug if available, otherwise fallback to ID
    const profilePath = mentorSlug ? `/mentores/${mentorSlug}` : `/mentores/${mentorId}`;
    console.log(`Navigating to mentor profile: ${profilePath}`);
    navigate(profilePath);
  };

  const refreshMentorships = () => {
    console.log("Manually refreshing mentorships data");
    fetchMentorships();
  };

  return {
    mentorships: filteredMentorships,
    mentorRatings,
    isLoading,
    error,
    games,
    searchTerm,
    selectedGameId,
    selectedType,
    handleSearchChange,
    handleGameFilterChange,
    handleTypeFilterChange,
    handleCardClick,
    refreshMentorships,
  };
};
